/**
 * Mock Backend User Database
 *
 * Pre-configured users for the frontend mock API system.
 * These accounts simulate a real backend user database for offline development.
 */

import { USER_ROLES } from '@/types/user'
import type { User } from '@/types/api'

/**
 * Mock user extends the real User type with additional mock-specific fields
 */
export interface MockUser extends User {
  otp: string // Mock OTP code for authentication testing
  description: string // Human-readable description for developers
}

/**
 * Mock user database with pre-configured accounts for all roles
 * Used by the mock API to simulate different user permissions
 */
export const MOCK_USERS: Record<string, MockUser> = {
  GUEST: {
    id: 'mock-guest-001',
    phone: '+10001112222',
    email: 'guest@mock.test',
    role: USER_ROLES.GUEST,
    otp: '000000',
    description: 'Guest account with minimal permissions',
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
    deletedAt: null,
    suspendedAt: null,
    suspendedReason: null,
    suspendedBy: null,
  },
  USER: {
    id: 'mock-user-001',
    phone: '+11112223333',
    email: 'user@mock.test',
    role: USER_ROLES.USER,
    otp: '111111',
    description: 'Regular user account',
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
    deletedAt: null,
    suspendedAt: null,
    suspendedReason: null,
    suspendedBy: null,
  },
  MODERATOR: {
    id: 'mock-mod-001',
    phone: '+12223334444',
    email: 'moderator@mock.test',
    role: USER_ROLES.MODERATOR,
    otp: '222222',
    description: 'Moderator account with content management permissions',
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
    deletedAt: null,
    suspendedAt: null,
    suspendedReason: null,
    suspendedBy: null,
  },
  EDITOR: {
    id: 'mock-editor-001',
    phone: '+13334445555',
    email: 'editor@mock.test',
    role: USER_ROLES.EDITOR,
    otp: '333333',
    description: 'Editor account with content editing permissions',
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
    deletedAt: null,
    suspendedAt: null,
    suspendedReason: null,
    suspendedBy: null,
  },
  ADMIN: {
    id: 'mock-admin-001',
    phone: '+14445556666',
    email: 'admin@mock.test',
    role: USER_ROLES.ADMIN,
    otp: '444444',
    description: 'Admin account with full system access',
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
    deletedAt: null,
    suspendedAt: null,
    suspendedReason: null,
    suspendedBy: null,
  },
  OWNER: {
    id: 'mock-owner-001',
    phone: '+15556667777',
    email: 'owner@mock.test',
    role: USER_ROLES.OWNER,
    otp: '555555',
    description: 'Owner account with highest privileges',
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
    deletedAt: null,
    suspendedAt: null,
    suspendedReason: null,
    suspendedBy: null,
  },
}

/**
 * Get mock user by phone number
 */
export function getMockUserByPhone(phone: string): MockUser | undefined {
  return Object.values(MOCK_USERS).find((user) => user.phone === phone)
}

/**
 * Get mock user by role
 */
export function getMockUserByRole(role: number): MockUser | undefined {
  return Object.values(MOCK_USERS).find((user) => user.role === role)
}

/**
 * Check if a phone number belongs to a mock user
 */
export function isMockUser(phone: string): boolean {
  return Object.values(MOCK_USERS).some((user) => user.phone === phone)
}

/**
 * Get all mock user phone numbers
 */
export function getAllMockPhones(): string[] {
  return Object.values(MOCK_USERS)
    .map((user) => user.phone)
    .filter((phone): phone is string => phone != null)
}

/**
 * Get mock user by ID
 */
export function getMockUserById(id: string): MockUser | undefined {
  return Object.values(MOCK_USERS).find((user) => user.id === id)
}
